import React, { useCallback, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnalysisPanel } from 'components/panel/AnalysisPanel';
import { PanelToolbar } from './PanelToolbar';
import './ComparisonGrid.css';

function makeId(seq: number): string {
  // crypto.randomUUID isn't guaranteed on http origins; a seq-based id is
  // stable, unique, and SSR/StrictMode-safe.
  return `panel-${seq}`;
}

interface SortablePanelProps {
  id: string;
  removable: boolean;
  sortable: boolean;
  onRemove: () => void;
}

// Wraps a panel as a sortable grid item. Crucially, the panel keeps its own
// internal state (live socket, analysis, selections) across reorders because
// the AnalysisPanel instance is preserved by its React key — we only move it.
function SortablePanel({ id, removable, sortable, onRemove }: SortablePanelProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: !sortable });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged panel above its neighbours during the swap.
    zIndex: isDragging ? 20 : undefined,
    position: isDragging ? 'relative' : undefined,
  };

  // Only offer the grip when there's something to reorder. The handle is the
  // sole drag activator, so the rest of the panel stays fully interactive.
  const dragHandle = sortable ? (
    <button
      type="button"
      className="panel-drag-handle"
      ref={setActivatorNodeRef}
      title="Drag to reorder"
      aria-label="Drag to reorder panel"
      {...attributes}
      {...listeners}
    >
      {/* Horizontal 3×2 grip dots; currentColor inherits the button's hover state. */}
      <svg width="16" height="10" viewBox="0 0 16 10" aria-hidden="true" focusable="false">
        <g fill="currentColor">
          <circle cx="3" cy="3" r="1.3" />
          <circle cx="8" cy="3" r="1.3" />
          <circle cx="13" cy="3" r="1.3" />
          <circle cx="3" cy="7" r="1.3" />
          <circle cx="8" cy="7" r="1.3" />
          <circle cx="13" cy="7" r="1.3" />
        </g>
      </svg>
    </button>
  ) : undefined;

  return (
    <div ref={setNodeRef} style={style} className={`sortable-panel${isDragging ? ' dragging' : ''}`}>
      <AnalysisPanel removable={removable} onRemove={onRemove} dragHandle={dragHandle} />
    </div>
  );
}

export function ComparisonGrid() {
  const seqRef = useRef(1);
  const [panelIds, setPanelIds] = useState<string[]>(() => [makeId(0)]);

  const addPanel = useCallback(() => {
    const id = makeId(seqRef.current++);
    setPanelIds((ids) => [...ids, id]);
  }, []);

  const removePanel = useCallback((id: string) => {
    setPanelIds((ids) => ids.filter((p) => p !== id));
  }, []);

  // PointerSensor with a small activation distance so a click on the grip (or a
  // nearby control) isn't misread as a drag. KeyboardSensor makes reordering
  // accessible (focus the grip, then arrow keys).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPanelIds((ids) => {
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      // Guard against a stale id (e.g. panel removed mid-drag).
      if (oldIndex === -1 || newIndex === -1) return ids;
      return arrayMove(ids, oldIndex, newIndex);
    });
  }, []);

  const canReorder = panelIds.length > 1;

  return (
    <div className="comparison-root">
      <PanelToolbar panelCount={panelIds.length} onAdd={addPanel} />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={panelIds} strategy={horizontalListSortingStrategy}>
          <div className="grid">
            {panelIds.map((id) => (
              <SortablePanel
                key={id}
                id={id}
                removable={canReorder}
                sortable={canReorder}
                onRemove={() => removePanel(id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
